import { memo, useContext, useEffect, useState } from 'react';
import { AppButton } from 'components';
import { useHistory, useParams } from 'react-router-dom';
import Editor from 'components/CKEditor/Editor';
import { ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import ShortAnswer from './ShortAnswer';
import MatchTheFollowing from './MatchTheFollowing';
import FillInTheBlanks from './FillInTheBlanks';
import MCQ from './MCQ';
import { getRandomUid } from 'utilities/uuid.service';
import * as AppPics from 'assets/images';
import { EditorPreviewContext } from 'context/EditorPreviewContext';
// import HandleDelete from '../../HandleDelete';
import useDebounce from 'hooks/useDebounce';
import useQueryParams, { QueryParamsAsString } from 'hooks/useQueryParams';
import QuizSectionsSettings from '../QuizSectionsSettings';

function SubQuestions({ editMode, toggleView, handleSave, data, configs }) {
  const history = useHistory();
  const { classID, quizID, courseID, chapterID } = useParams();
  const { PreviewBtn } = useContext(EditorPreviewContext);
  const queryParams = useQueryParams();
  const paperId = queryParams.get('paperId');

  const queryString = QueryParamsAsString();

  const { hideSaveBtn = false, hideTopBtnSection = false, deleteConfig } = configs ?? {};
  const { showRating } = configs ?? {};

  // const [disclaimer, setDisclaimer] = useState('');
  // const [explanation, setExplanation] = useState('');
  const [section, setSection] = useState();

  const [title, setTitle] = useState('');
  const [dropdownOpen, setOpen] = useState(false);
  const [qStatus, setQStatus] = useState('active');
  const [subQuestions, setSubQuestions] = useState([]);

  const [settings, setSettings] = useState({});

  const toggle = () => setOpen(!dropdownOpen);

  const [showForm, toggleShowForm] = toggleView ?? [];

  function handleQuizQuestion(quizFormData = {}) {
    if (!title) {
      window.alert('Please specify the question');
      return;
    }
    if (subQuestions.length === 0) {
      window.alert('Please specify one or more sub-questions');
      return;
    }

    if (Array.isArray(quizFormData.subQuestions)) {
      let hasError = false;
      for (let index = 0; index < quizFormData.subQuestions.length; index++) {
        const item = quizFormData.subQuestions[index];
        if (item.qPicker === 'choice') {
          const hasCorrectOption = item.data?.options.some((x) => x.isCorrect);
          if (item.category === 'single' && !hasCorrectOption) {
            hasError = true;
            window.alert(`Please specify a correct option in Q${index + 1}.`);
            break;
          }
          if (item.category === 'multiple' && !hasCorrectOption) {
            hasError = true;
            window.alert(`Please specify at least 1 correct option in Q${index + 1}.`);
            break;
          }
        }
        if (item.qPicker === 'fill') {
          if (!item.data?.title?.trim()) {
            hasError = true;
            window.alert(`Please specify the question in Q${index + 1}.`);
            break;
          }
          if (item.data?.options?.some((x) => !x.option?.trim())) {
            hasError = true;
            window.alert(`Please specify the options in Q${index + 1}.`);
            break;
          }
        }
        if (item.qPicker === 'match') {
          if (!item.data?.title?.trim()) {
            hasError = true;
            window.alert(`Please specify the question in Q${index + 1}.`);
            break;
          }
          if (item.data?.matchChoicesPartA?.some((x) => !x.option?.trim())) {
            hasError = true;
            window.alert(`Please specify the choices in Q${index + 1}.`);
            break;
          }
          if (item.data?.matchChoicesPartB?.some((x) => !x.option?.trim())) {
            hasError = true;
            window.alert(`Please specify the matching options in Q${index + 1}.`);
            break;
          }
        }
      }

      if (hasError) return;
    }

    handleSave({ ...quizFormData, paperId });
  }

  useEffect(() => {
    if (data?.data) {
      if (data?.data?.sectionId) setSection(data?.data?.sectionId);
      if (data?.data?.title) setTitle(data.data.title);
      if (data?.data?.status) setQStatus(data.data.status);
      // if (data?.data?.disclaimer) setDisclaimer(data.data.disclaimer);
      // if (data?.data?.explanation) setExplanation(data.data.explanation);
      if (Array.isArray(data?.data?.subQuestions) && data?.data?.subQuestions?.length > 0)
        setSubQuestions(data.data.subQuestions);

      const __settings = {
        difficultyLevel: data?.data?.difficultyLevel,
        assessmentObjective: data?.data?.assessmentObjective,
        examBoard: data?.data?.examBoard,
        askedInYear: data?.data?.askedInYear,
      };
      setSettings((x) => ({ ...x, ...__settings }));
    }
  }, [data?.data]);
  const [currentEdited, updateCurrentEditBlock] = useState({});

  const editorData = useDebounce(currentEdited, 100);

  useEffect(() => {
    if (editorData.id) {
      updateCurrentEditBlock(editorData);
      if (['choice', 'short', 'match', 'fill'].includes(editorData.qPicker)) {
        setSubQuestions(
          subQuestions.map((x) => {
            if (x.id === editorData.id) {
              return {
                ...x,
                data: {
                  ...x.data,
                  ...editorData.data,
                },
              };
            }
            return x;
          })
        );
      }
    }
  }, [editorData]);

  return (
    <div className="tab__content-actBody">
      <div className="px-5">
        {!hideTopBtnSection && (
          <div className="row justify-content-end my-4 ">
            <div className="col-12">
              {!editMode && (
                <AppButton
                  variantClass="btn-plane p--tint mx-2"
                  onClick={() => {
                    toggleShowForm(!showForm);
                  }}
                >
                  Change question type
                </AppButton>
              )}
              <AppButton
                variantClass="btn-plane mx-2"
                onClick={() => {
                  history.push(
                    `/classes/${classID}/courses/${courseID}/chapters/${chapterID}/quizzes/${quizID}/questions${queryString}`
                  );
                }}
              >
                Back to questions list
              </AppButton>
            </div>
          </div>
        )}

        <div className="row mt-4">
          <div className="col-md-12 col-lg-12 col-sm-12">
            <div className="lessonWrap lessonWrap--border topalign ___sub___questions mb-4">
              <div className="my-4">
                <div className="w-100 p-3">
                  <QuizSectionsSettings
                    selectedItemId={section}
                    selectedPaperId={paperId}
                    handleChange={(id) => setSection(id)}
                    prefix={'subQs' + data?.data?._id}
                    extras={{
                      settings,
                      setSettings: (item = {}) => setSettings((x) => ({ ...x, ...item })),
                    }}
                  />
                </div>
              </div>

              <div className="w-100 p-3">
                <label htmlFor="questionToFill">Main Question</label>
                {title && <PreviewBtn block={{ value: title }} />}
                <Editor
                  data={{
                    value: title,
                    onChange: (data) => setTitle(data),
                  }}
                  config={{
                    placeholder: 'Enter only the question here.',
                  }}
                />
              </div>

              <div className="col-12">
                <ButtonDropdown isOpen={dropdownOpen} toggle={toggle}>
                  <DropdownToggle caret className="__pinkBgColor">
                    Add Question types
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => {
                        setSubQuestions(
                          subQuestions.concat([
                            {
                              id: getRandomUid(),
                              qPicker: 'choice',
                              category: 'single',
                              data: {
                                questionType: 'choice',
                                title: '',
                                options: [
                                  { id: getRandomUid(), option: '', isCorrect: false },
                                  { id: getRandomUid(), option: '', isCorrect: false },
                                ],
                                status: 'active',
                              },
                            },
                          ])
                        );
                      }}
                    >
                      MCQ
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        setSubQuestions(
                          subQuestions.concat([
                            {
                              id: getRandomUid(),
                              qPicker: 'fill',
                              category: '',
                              data: {
                                questionType: 'fill',
                                title: '',
                                options: [{ id: getRandomUid(), option: '' }],
                                status: 'active',
                              },
                            },
                          ])
                        );
                      }}
                    >
                      Fill In the Blanks
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        setSubQuestions(
                          subQuestions.concat([
                            {
                              id: getRandomUid(),
                              qPicker: 'match',
                              category: '',
                              data: {
                                questionType: 'match',
                                title: '',
                                status: 'active',
                                matchChoicesPartA: [{ id: getRandomUid(), option: '' }],
                                matchChoicesPartB: [{ id: getRandomUid(), option: '' }],
                              },
                            },
                          ])
                        );
                      }}
                    >
                      Match the Following
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        setSubQuestions(
                          subQuestions.concat([
                            {
                              id: getRandomUid(),
                              qPicker: 'short',
                              category: '',
                              data: {
                                questionType: 'short',
                                answer: '',
                                title: '',
                                status: 'active',
                              },
                            },
                          ])
                        );
                      }}
                    >
                      Short Answer
                    </DropdownItem>
                  </DropdownMenu>
                </ButtonDropdown>
              </div>

              {subQuestions.map((item) => {
                return (
                  <div key={item.id} className="position-relative">
                    <AppButton
                      variantClass="lessonWrap__btn leftpos delete"
                      style={{ zIndex: 1 }}
                      onClick={() => {
                        setSubQuestions(subQuestions.filter((x) => x.id !== item.id));
                      }}
                    >
                      <img src={AppPics.DeleteSvg} alt="delete this option" />
                    </AppButton>
                    {item.qPicker === 'choice' && (
                      <MCQ
                        data={item.data}
                        handleSave={(data = {}) => {
                          updateCurrentEditBlock({ qPicker: 'choice', id: item.id, data });
                          // setSubQuestions(
                          //   subQuestions.map((x) => {
                          //     if (x.id === item.id) {
                          //       return {
                          //         ...x,
                          //         data: {
                          //           ...x.data,
                          //           ...data,
                          //         },
                          //       };
                          //     }
                          //     return x;
                          //   })
                          // );
                        }}
                        selectedCategory={[
                          item.category,
                          (data) => {
                            setSubQuestions(
                              subQuestions.map((x) => {
                                if (x.id === item.id) x.category = data;
                                return x;
                              })
                            );
                          },
                        ]}
                      />
                    )}
                    {item.qPicker === 'fill' && (
                      <FillInTheBlanks
                        data={item.data}
                        handleSave={(data = {}) => {
                          updateCurrentEditBlock({ qPicker: 'fill', id: item.id, data });
                          // setSubQuestions(
                          //   subQuestions.map((x) => {
                          //     if (x.id === item.id) {
                          //       return {
                          //         ...x,
                          //         data: {
                          //           ...x.data,
                          //           ...data,
                          //         },
                          //       };
                          //     }
                          //     return x;
                          //   })
                          // );
                        }}
                      />
                    )}
                    {item.qPicker === 'match' && (
                      <MatchTheFollowing
                        data={item.data}
                        handleSave={(data = {}) => {
                          updateCurrentEditBlock({ qPicker: 'match', id: item.id, data });
                          //   setSubQuestions(
                          //     subQuestions.map((x) => {
                          //       if (x.id === item.id) {
                          //         return {
                          //           ...x,
                          //           data: {
                          //             ...x.data,
                          //             ...data,
                          //           },
                          //         };
                          //       }
                          //       return x;
                          //     })
                          //   );
                        }}
                      />
                    )}
                    {item.qPicker === 'short' && (
                      <ShortAnswer
                        data={item.data}
                        handleSave={(data = {}) => {
                          updateCurrentEditBlock({ qPicker: 'short', id: item.id, data });
                          // setSubQuestions(
                          //   subQuestions.map((x) => {
                          //     if (x.id === item.id) {
                          //       return {
                          //         ...x,
                          //         data: {
                          //           ...x.data,
                          //           ...data,
                          //         },
                          //       };
                          //     }
                          //     return x;
                          //   })
                          // );
                        }}
                      />
                    )}
                  </div>
                );
              })}

              {/* <div className="w-100 p-3 mt-4">
                <label htmlFor="">Hint</label>
                {disclaimer && <PreviewBtn block={{ value: disclaimer }} />}
                <Editor
                  data={{
                    value: disclaimer,
                    onChange: (data) => setDisclaimer(data),
                  }}
                />
              </div> */}
              {/* <div className="w-100 p-3">
                <label htmlFor="">Explanation</label>
                {explanation && <PreviewBtn block={{ value: explanation }} />}
                <Editor
                  data={{
                    value: explanation,
                    onChange: (data) => setExplanation(data),
                  }}
                />
              </div> */}

              {/* {!hideSaveBtn && ( */}
              {!showRating && (
                <div className="mb-5 btnTab-foot mt-5">
                  <AppButton
                    className="btn-right-content save"
                    onClick={() => {
                      handleQuizQuestion({
                        title,
                        subQuestions,
                        status: qStatus,
                        questionType: 'subQuestions',
                        section,
                        // disclaimer,
                        // explanation,
                        ...settings,
                      });
                    }}
                  >
                    Save Changes
                  </AppButton>
                  {/* {deleteConfig?.showBtn && (
                  <HandleDelete handleDelete={deleteConfig?.handleDelete} />
                )} */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(SubQuestions);
